import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import RecommendationCard from './RecommendationCard';
import { recordDecisionBriefFeedback, recommendationEvidenceHref } from '../../utils/recommendations';
import Tooltip from '../common/Tooltip';
import { useAuth } from '../auth/AuthContext';

export default function DecisionBrief({ brief, canAct, busy, handlers, onViewTab, onToast }) {
  const { currentUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const submit = async useful => {
    setSaving(true);
    setError('');
    try {
      await recordDecisionBriefFeedback(brief, useful, currentUser);
      setSubmitted(true);
      onToast?.({ message: 'Decision brief feedback saved.', type: 'success' });
    } catch (err) {
      console.error('Decision brief feedback failed:', err);
      setError(err.message || 'Could not save feedback.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <section aria-label="Customer decision brief" className="border border-indigo-200 rounded-lg p-4 space-y-3 bg-indigo-50 min-w-0">
      <h4 className="font-semibold text-gray-900">Recommended move</h4>
      <p className="text-xs text-gray-600">Rules-based decision support, not a prediction. Review before taking action.</p>
      <p className="text-xs text-gray-600">
        Data refreshed: {brief.generatedAt.toDate().toLocaleString()}. Selected using existing priority and confidence.
      </p>
      <RecommendationCard rec={brief.recommendation} canAct={canAct} busy={busy} onViewTab={onViewTab} {...handlers} />
      <div>
        <h5 className="text-sm font-semibold">Observed evidence</h5>
        <ul className="list-disc pl-5 text-sm text-gray-700">
          {brief.evidence.map((ref, index) => {
            const href = recommendationEvidenceHref(ref);
            return <li key={`${ref.kind}-${ref.refId}-${index}`} className="break-words">
              {ref.fact}{' '}
              {href ? <Link to={href} className="text-indigo-700 underline">{ref.kind}: {ref.refId}</Link>
                : <span className="text-xs">({ref.kind}: {ref.refId})</span>}
            </li>;
          })}
        </ul>
      </div>
      <div className="text-sm">
        <h5 className="font-semibold">What is still unknown</h5>
        <p>{brief.uncertainty}</p>
        {brief.missing.map(item => <p key={item}>{item}</p>)}
      </div>
      <div className="text-sm"><h5 className="font-semibold">One question for the rep to ask</h5><p>{brief.question}</p></div>
      {canAct && (
        <div className="flex flex-wrap gap-2 items-center">
          {submitted ? <p role="status" className="text-sm">Thanks for your feedback.</p> : <>
            <span className="text-sm">Was this brief useful?</span>
            {[true, false].map(useful => (
              <Tooltip key={String(useful)} text={`Record this brief as ${useful ? 'useful' : 'not useful'} without changing the recommendation`}>
                <button type="button" disabled={saving} onClick={() => submit(useful)}
                  className="border border-indigo-300 rounded px-3 py-1 text-sm disabled:opacity-50">
                  {useful ? 'Useful' : 'Not useful'}
                </button>
              </Tooltip>
            ))}
          </>}
        </div>
      )}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    </section>
  );
}
